import {
  Chip,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import React, { useContext, useMemo, useState } from "react";
import { ProjectContext } from "../../contexts/ProjectContext.tsx";
import { sortEncounterCodes } from "../../models/patient.ts";
import { getDiffDaysMessage } from "../../utils/main.ts";
import { TableRowsSkeleton } from "./TableRowsSkeleton.js";

interface CodeListTableProps {
  codes?: CodeBase[];
  loading: boolean;
  onOpenVisit: (encounterId: string) => void;
}

type SortField = "code" | "description" | "visitDiffDays" | "codeDiffDays";
type SortDirection = "asc" | "desc";

export default function CodeListTable({
  codes,
  loading,
  onOpenVisit,
}: CodeListTableProps) {
  const { highlightDate } = useContext(ProjectContext)!;

  const [sortBy, setSortBy] = useState<SortField>("visitDiffDays");
  const [sort, setSort] = useState<SortDirection>("asc");

  const codesSorted = useMemo(() => {
    if (!codes || codes.length === 0) {
      return [];
    }
    const dataUnsorted = [...codes];
    return sortEncounterCodes(sort, sortBy, dataUnsorted);
  }, [sort, sortBy, codes]);

  function sortColumn(columnToSort: SortField) {
    if (!sortBy) {
      return;
    }
    if (sort === "desc" || sortBy !== columnToSort) {
      setSort("asc");
      setSortBy(columnToSort);
      return;
    }
    setSort("desc");
    setSortBy(columnToSort);
  }

  return (
    <TableContainer>
      <Table className={"code-list-table"} stickyHeader={true}>
        <TableHead className={"head"}>
          <TableRow>
            <TableCell className={"code"}>
              <TableSortLabel
                active={sortBy === "code"}
                direction={sort}
                onClick={() => sortColumn("code")}
              >
                Code
              </TableSortLabel>
            </TableCell>
            <TableCell className="code-description">
              <TableSortLabel
                active={sortBy === "description"}
                direction={sort}
                onClick={() => sortColumn("description")}
              >
                Code Description
              </TableSortLabel>
            </TableCell>
            <TableCell className="code-date">
              <TableSortLabel
                active={sortBy === "codeDiffDays"}
                direction={sort}
                onClick={() => sortColumn("codeDiffDays")}
              >
                Code Date
              </TableSortLabel>
            </TableCell>
            <TableCell className="visit-date">
              <TableSortLabel
                active={sortBy === "visitDiffDays"}
                direction={sort}
                onClick={() => sortColumn("visitDiffDays")}
              >
                Visit Date
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody className={"body"} sx={{ bgcolor: "background.paper" }}>
          {loading ? (
            <TableRowsSkeleton rowsNum={5} columnsNum={3} />
          ) : (
            codesSorted.map((code, code_index) => {
              return (
                <TableRow
                  key={
                    code.encounterId + code.code + code.type + "_" + code_index
                  }
                  sx={{
                    backgroundColor:
                      highlightDate && new Date(code.createdAt) >= highlightDate
                        ? "highlight.main"
                        : undefined,
                  }}
                >
                  <TableCell>
                    <Chip
                      classes={{ root: "chip-container", label: "chip-label" }}
                      label={code.code}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{code.description}</TableCell>
                  <TableCell>{getDiffDaysMessage(code.codeDiffDays)}</TableCell>
                  <TableCell>
                    <Link
                      component="button"
                      variant="body2"
                      align="left"
                      onClick={() => onOpenVisit(code.encounterId)}
                      className={code.indexProcedure ? "index-procedure" : ""}
                    >
                      {code.indexProcedure
                        ? "Index"
                        : getDiffDaysMessage(code.visitDiffDays)}
                    </Link>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
